<template>
  <main class="scenario-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">受控模板 · 自动验证</span>
        <h1>场景部署中心</h1>
      </div>
      <div class="header-actions">
        <el-button @click="refreshAll" :loading="loading">刷新</el-button>
        <el-button type="primary" @click="$router.push('/multi-instances')">管理虚拟机</el-button>
      </div>
    </header>

    <section class="resource-summary">
      <div class="resource-main">
        <span class="status-dot" :class="{ online: resources.instances.length }"></span>
        <strong>当前资源</strong>
        <template v-if="resources.instances.length">
          <span>{{ resources.instances.length }} 台实例</span>
          <span>{{ displayDistribution }}</span>
          <span>{{ resources.profile.spec || "规格未知" }}</span>
          <span>剩余 {{ resources.remaining_minutes }} 分钟</span>
        </template>
        <span v-else>{{ provisioning ? "正在创建 Ubuntu 24.04 实例" : "尚未准备虚拟机，可在本页一键创建并部署" }}</span>
      </div>
      <el-button v-if="resources.instances.length" link type="primary" @click="resourceExpanded = !resourceExpanded">
        {{ resourceExpanded ? "收起" : "查看实例" }}
      </el-button>
      <el-button v-else-if="!provisioning" link type="primary" @click="scrollToConfiguration">选择场景并创建</el-button>
      <el-tag v-else type="warning">{{ provisionStatusText }}</el-tag>
    </section>

    <el-collapse-transition>
      <section v-if="resourceExpanded && resources.instances.length" class="resource-details">
        <div class="release-time">统一释放时间：{{ resources.release_at }}</div>
        <div class="instance-chips">
          <button
            v-for="instance in resources.instances"
            :key="instance.instance_id"
            :class="['instance-chip', { selected: selectedInstanceId === instance.instance_id }]"
            @click="selectedInstanceId = instance.instance_id"
          >
            <strong>{{ instance.label }}</strong>
            <span>{{ instance.public_ipv4 || instance.private_ipv4 }}</span>
          </button>
        </div>
      </section>
    </el-collapse-transition>

    <section class="console-shell">
      <el-tabs v-model="activeTab" class="console-tabs">
        <el-tab-pane label="标准部署" name="catalog">
          <div class="deploy-workspace">
            <aside class="catalog-panel">
              <div class="catalog-tools">
                <el-input v-model="templateSearch" clearable placeholder="搜索场景" />
                <el-select v-model="selectedCategory" class="category-select">
                  <el-option label="全部分类" value="" />
                  <el-option v-for="category in categories" :key="category" :label="category" :value="category" />
                </el-select>
              </div>
              <div class="catalog-count">{{ filteredTemplates.length }} 个可部署场景</div>
              <div class="template-list" v-loading="loading">
                <button
                  v-for="item in filteredTemplates"
                  :key="item.id"
                  :class="['template-row', { selected: selectedTemplateId === item.id }]"
                  @click="selectTemplate(item.id)"
                >
                  <span :class="['template-icon', item.icon]">{{ templateInitial(item) }}</span>
                  <span class="template-row-copy">
                    <strong>{{ item.name }}</strong>
                    <small>{{ item.category }} · 约 {{ item.estimated_minutes }} 分钟</small>
                  </span>
                  <span class="row-arrow">›</span>
                </button>
                <el-empty v-if="!loading && !filteredTemplates.length" description="没有匹配的场景" :image-size="64" />
              </div>
            </aside>

            <section class="config-panel">
              <template v-if="selectedTemplate">
                <div class="config-head">
                  <div>
                    <span class="eyebrow">{{ selectedTemplate.category }}</span>
                    <h2>{{ selectedTemplate.name }}</h2>
                    <p>{{ selectedTemplate.summary }}</p>
                  </div>
                  <el-tag effect="plain">v{{ selectedTemplate.version }}</el-tag>
                </div>

                <div class="feature-list">
                  <el-tag v-for="feature in selectedTemplate.features" :key="feature" effect="plain">
                    {{ feature }}
                  </el-tag>
                </div>

                <div class="config-grid">
                  <div class="config-item">
                    <span>软件版本</span>
                    <el-select v-if="selectedTemplate.software_versions?.length" v-model="selectedSoftwareVersion" size="small">
                      <el-option v-for="version in selectedTemplate.software_versions" :key="version.value" :label="version.label" :value="version.value" />
                    </el-select>
                    <strong v-else>系统仓库版</strong>
                    <small>平台已验证</small>
                  </div>
                  <div class="config-item">
                    <span>节点要求</span>
                    <strong>{{ selectedTemplate.min_nodes }} 台</strong>
                    <small>最低 {{ selectedTemplate.minimum_spec }} · 当前 {{ resources.profile.spec || "未知" }}</small>
                  </div>
                  <div class="config-item">
                    <span>预计耗时</span>
                    <strong>{{ selectedTemplate.estimated_minutes }} 分钟</strong>
                    <small>以实际网络为准</small>
                  </div>
                  <div class="config-item">
                    <span>场景服务费</span>
                    <strong>{{ selectedTemplate.service_fee_points }} 积分</strong>
                    <small>VIP 基础场景免费</small>
                  </div>
                </div>

                <div class="target-section">
                  <div class="field-label">选择目标实例</div>
                  <el-radio-group v-if="resources.instances.length && requiredNodes === 1" v-model="selectedInstanceId">
                    <el-radio
                      v-for="instance in resources.instances"
                      :key="instance.instance_id"
                      :label="instance.instance_id"
                      border
                    >
                      {{ instance.label }} · {{ instance.public_ipv4 || instance.private_ipv4 }}
                    </el-radio>
                  </el-radio-group>
                  <el-checkbox-group v-else-if="resources.instances.length" v-model="selectedInstanceIds" :max="requiredNodes">
                    <el-checkbox
                      v-for="instance in resources.instances"
                      :key="instance.instance_id"
                      :label="instance.instance_id"
                      border
                    >
                      {{ instance.label }} · {{ instance.public_ipv4 || instance.private_ipv4 }}
                    </el-checkbox>
                  </el-checkbox-group>
                  <div v-else class="auto-provision-card">
                    <div>
                      <strong>自动创建 Ubuntu 24.04 LTS</strong>
                      <p>将按场景最低要求创建 {{ requiredNodes }} 台 {{ recommendedSpec }} Spot 实例，系统盘 40GB，使用 1 小时后自动释放。</p>
                    </div>
                    <el-tag effect="plain">云资源正常扣积分</el-tag>
                  </div>
                </div>

                <div v-if="preflightResult" class="check-list">
                  <div v-for="check in preflightResult.checks" :key="check.key" :class="['check-item', { failed: !check.passed }]">
                    <span>{{ check.passed ? "✓" : "!" }}</span>
                    <p>{{ check.message }}</p>
                  </div>
                </div>

                <div class="deploy-footer">
                  <span>{{ resources.instances.length ? `目标：${selectedTargetLabel}` : `将创建：${requiredNodes} 台 Ubuntu 24.04 · ${recommendedSpec}` }}</span>
                  <el-button
                    type="primary"
                    size="large"
                    :loading="checking || submitting || provisioning"
                    :disabled="!selectedTemplate || isDeploymentActive || (resources.instances.length > 0 && !hasEnoughTargets)"
                    @click="handlePrimaryAction"
                  >
                    {{ primaryActionText }}
                  </el-button>
                </div>
              </template>
              <el-empty v-else description="从左侧选择一个部署场景" />
            </section>
          </div>
        </el-tab-pane>

        <el-tab-pane label="智能部署" name="ai">
          <div class="ai-workspace">
            <div class="ai-copy">
              <span class="eyebrow">智能规划 · 受控执行</span>
              <h2>描述你想要的工程环境</h2>
              <p>系统只会选择平台审核过的模板，不会直接生成并执行任意命令。</p>
              <div class="prompt-examples">
                <button @click="prompt = '安装 Docker'">安装 Docker</button>
                <button @click="prompt = '给这台机器部署 Nginx'">部署 Nginx</button>
                <button @click="prompt = '安装 JDK 21 开发环境'">安装 JDK 21</button>
                <button @click="prompt = '部署 Elasticsearch 搜索服务'">部署 Elasticsearch</button>
                <button @click="prompt = '部署三节点 Kubernetes 集群'">部署 Kubernetes</button>
                <button @click="prompt = '部署轻量 K3s 集群'">部署 K3s</button>
                <button @click="prompt = '部署四节点 MinIO 对象存储集群'">部署 MinIO</button>
                <button @click="prompt = '部署 Jenkins 流水线平台'">部署 Jenkins</button>
                <button @click="prompt = '用 Prometheus 监控三台主机'">部署主机监控</button>
              </div>
            </div>
            <div class="intent-box">
              <el-input
                v-model="prompt"
                type="textarea"
                :rows="5"
                maxlength="200"
                show-word-limit
                placeholder="例如：给这台 Ubuntu 机器安装 Docker，并完成健康检查"
              />
              <el-button type="primary" :loading="interpreting" @click="interpretPrompt">生成部署计划</el-button>
              <div v-if="selectedTemplate" class="recognized-plan">
                <span>当前识别结果</span>
                <strong>{{ selectedTemplate.name }}</strong>
                <el-button link type="primary" @click="activeTab = 'catalog'">查看并确认参数</el-button>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane name="active-scenes">
          <template #label>
            <span>活跃场景 <el-badge v-if="activeScenarios.length" :value="activeScenarios.length" /></span>
          </template>
          <div class="active-scenes-view">
            <div class="active-scenes-head">
              <div><h2>当前实例上的场景</h2><p>仅展示仍绑定活跃实例的成功部署记录。</p></div>
              <el-button :loading="loadingActiveScenarios" @click="loadActiveScenarios">刷新检测</el-button>
            </div>
            <div v-if="activeScenarios.length" class="active-scene-grid">
              <article v-for="scene in activeScenarios" :key="`${scene.template_id}-${scene.instance.instance_id}`" class="active-scene-card">
                <div class="scene-card-head">
                  <div><span class="eyebrow">运行中 · {{ scene.instance.label }}{{ scene.role ? ` · ${scene.role}` : "" }}</span><h3>{{ scene.template_name }}</h3></div>
                  <el-tag type="success">{{ scene.software_version || `v${scene.template_version}` }}</el-tag>
                </div>
                <div class="scene-meta"><span>部署于 {{ scene.deployed_at }}</span><span>释放于 {{ scene.release_at }}</span></div>
                <div class="scene-address">
                  <span>公网 IP</span><code>{{ scene.public_ip || "未分配" }}</code>
                  <el-button v-if="scene.public_ip" link type="primary" @click="copyText(scene.public_ip)">复制</el-button>
                </div>
                <div class="scene-address">
                  <span>内网 IP</span><code>{{ scene.private_ip || "未分配" }}</code>
                  <el-button v-if="scene.private_ip" link type="primary" @click="copyText(scene.private_ip)">复制</el-button>
                </div>
                <div v-if="scene.credentials" class="credential-box">
                  <div><span>用户名</span><code>{{ scene.credentials.username }}</code><el-button link type="primary" @click="copyText(scene.credentials.username)">复制</el-button></div>
                  <div><span>密码</span><code>{{ revealedSecrets[scene.deployment_id] ? scene.credentials.password : "••••••••••" }}</code><el-button link @click="toggleSecret(scene.deployment_id)">{{ revealedSecrets[scene.deployment_id] ? "隐藏" : "显示" }}</el-button><el-button link type="primary" @click="copyText(scene.credentials.password)">复制</el-button></div>
                </div>
                <div class="connection-list">
                  <div v-for="item in scene.connections" :key="item.label"><span>{{ item.label }}</span><code>{{ item.command }}</code><el-button link type="primary" @click="copyText(item.command)">复制</el-button></div>
                </div>
                <div v-if="scene.data_job" class="data-job-status">
                  <span>数据注入</span>
                  <el-tag :type="dataJobTag(scene.data_job.status)">{{ dataJobText(scene.data_job) }}</el-tag>
                  <small v-if="scene.data_job.result">数据库 {{ scene.data_job.result.database }} · {{ scene.data_job.result.orders }} 笔订单</small>
                  <small v-else-if="scene.data_job.error_message" class="data-error">{{ scene.data_job.error_message }}</small>
                </div>
                <el-button v-if="scene.supports_data_injection" class="inject-data-button" type="primary" plain :disabled="['QUEUED','RUNNING'].includes(scene.data_job?.status)" @click="openDataInjection(scene)">
                  数据注入
                </el-button>
              </article>
            </div>
            <el-empty v-else-if="!loadingActiveScenarios" description="当前实例还没有成功部署的场景" />
          </div>
        </el-tab-pane>

        <el-tab-pane name="deployments">
          <template #label>
            <span>我的部署 <el-badge v-if="isDeploymentActive" is-dot /></span>
          </template>
          <div class="deployments-view">
            <div v-if="currentDeployment" class="active-task-card" @click="deploymentDrawer = true">
              <div>
                <span class="eyebrow">最近任务</span>
                <h3>{{ deploymentStatusText }}</h3>
                <p>{{ currentDeployment.current_step }}</p>
              </div>
              <div class="task-progress">
                <el-progress type="circle" :width="66" :percentage="currentDeployment.progress || 0" />
                <el-button link type="primary">查看详情</el-button>
              </div>
            </div>

            <div v-if="history.length" class="history-hint">点击任意部署记录，或使用右侧“查看详情”，可查看步骤、结果与错误信息。</div>
            <el-table v-if="history.length" :data="history" size="small" class="clickable-deployment-table" @row-click="openHistoryDeployment">
              <el-table-column prop="created_at" label="时间" width="180" />
              <el-table-column prop="template_id" label="场景" />
              <el-table-column prop="template_version" label="版本" width="100" />
              <el-table-column prop="service_fee_points" label="服务费" width="100" />
              <el-table-column label="状态" width="120">
                <template #default="scope"><el-tag :type="statusTag(scope.row.status)">{{ scope.row.status }}</el-tag></template>
              </el-table-column>
              <el-table-column label="操作" width="120" align="right">
                <template #default="scope"><el-button link type="primary" @click.stop="openHistoryDeployment(scope.row)">查看详情 <span aria-hidden="true">›</span></el-button></template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="还没有部署记录" />
          </div>
        </el-tab-pane>
      </el-tabs>
    </section>

    <el-dialog v-model="dataDialogVisible" title="注入数据库演示数据" width="480px">
      <el-form label-position="top">
        <el-form-item label="目标场景"><el-input :model-value="dataTargetScene?.template_name || ''" disabled /></el-form-item>
        <el-form-item label="数据集">
          <el-select v-model="dataForm.dataset_key" style="width:100%"><el-option label="电商订单数据（客户、商品、订单、明细）" value="ecommerce" /></el-select>
        </el-form-item>
        <el-form-item label="数据量">
          <el-radio-group v-model="dataForm.dataset_size">
            <el-radio-button label="small">小型 · 500 笔订单</el-radio-button>
            <el-radio-button label="medium">中型 · 5000 笔订单</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-alert title="再次注入会重建 sre_shop 演示库中的数据；主备场景只写主库并由复制链路同步。" type="info" :closable="false" show-icon />
      </el-form>
      <template #footer><el-button @click="dataDialogVisible=false">取消</el-button><el-button type="primary" :loading="injectingData" @click="submitDataInjection">开始注入</el-button></template>
    </el-dialog>

    <button v-if="isDeploymentActive && !deploymentDrawer" class="floating-task" @click="deploymentDrawer = true">
      <span class="pulse"></span>
      {{ deploymentStatusText }} · {{ currentDeployment.progress || 0 }}%
    </button>

    <el-drawer v-model="deploymentDrawer" title="部署任务" size="520px">
      <template v-if="currentDeployment">
        <div class="drawer-status">
          <div>
            <span class="eyebrow">{{ currentDeployment.template_id }}</span>
            <h2>{{ deploymentStatusText }}</h2>
            <p>{{ currentDeployment.current_step }}</p>
          </div>
          <el-tag :type="deploymentTagType" size="large">{{ currentDeployment.status }}</el-tag>
        </div>
        <el-progress
          :percentage="currentDeployment.progress || 0"
          :status="currentDeployment.status === 'FAILED' ? 'exception' : currentDeployment.status === 'SUCCEEDED' ? 'success' : undefined"
        />

        <el-collapse v-model="expandedDeploymentSections" class="deployment-details-collapse">
          <el-collapse-item name="steps" title="执行详情（命令与参数已脱敏）">
            <div class="timeline">
              <div v-for="(event, index) in currentDeployment.events || []" :key="index" :class="['event', event.level.toLowerCase()]">
                <time>{{ event.created_at }}</time>
                <div><strong>{{ event.step }}</strong><code v-if="event.step === '执行计划'">{{ event.message }}</code><span v-else>{{ event.message }}</span></div>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>

        <el-alert
          v-if="currentDeployment.status === 'FAILED'"
          :title="currentDeployment.error_message || '部署失败，服务费已解冻'"
          type="error"
          show-icon
          :closable="false"
        />

        <section v-if="infoCard" class="result-card">
          <div class="result-head"><h3>{{ infoCard.title }}</h3><el-tag type="success">{{ infoCard.status }}</el-tag></div>
          <div v-for="entry in infoCardEntries" :key="entry.key" class="result-row">
            <span>{{ entry.label }}</span>
            <strong>{{ entry.value }}</strong>
            <el-button v-if="entry.copyable" link type="primary" @click="copyText(entry.value)">复制</el-button>
          </div>
        </section>
      </template>
      <el-empty v-else description="暂无任务详情" />
    </el-drawer>
  </main>
</template>

<script>
import axios from "axios";
import { ElMessage, ElMessageBox } from "element-plus";

const emptyResources = () => ({ instances: [], profile: {}, remaining_minutes: 0, release_at: "" });

export default {
  name: "ScenarioLab",
  data() {
    return {
      loading: false,
      interpreting: false,
      checking: false,
      submitting: false,
      provisioning: false,
      provisionJobId: "",
      provisionStatusText: "准备创建",
      activeTab: "catalog",
      resourceExpanded: false,
      deploymentDrawer: false,
      templateSearch: "",
      selectedCategory: "",
      prompt: "",
      templates: [],
      resources: emptyResources(),
      selectedTemplateId: "",
      selectedSoftwareVersion: "",
      selectedInstanceId: "",
      selectedInstanceIds: [],
      preflightResult: null,
      currentDeployment: null,
      history: [],
      activeScenarios: [],
      loadingActiveScenarios: false,
      revealedSecrets: {},
      expandedDeploymentSections: [],
      dataDialogVisible: false,
      dataTargetScene: null,
      dataForm: { dataset_key: "ecommerce", dataset_size: "small" },
      injectingData: false,
      dataPollTimer: null,
      pollTimer: null,
      provisionPollTimer: null,
    };
  },
  computed: {
    categories() {
      return [...new Set(this.templates.map((item) => item.category).filter(Boolean))];
    },
    filteredTemplates() {
      const keyword = this.templateSearch.trim().toLowerCase();
      return this.templates.filter((item) => {
        const matchesCategory = !this.selectedCategory || item.category === this.selectedCategory;
        const text = [item.name, item.summary, item.category, ...(item.features || [])].join(" ").toLowerCase();
        return matchesCategory && (!keyword || text.includes(keyword));
      });
    },
    selectedTemplate() {
      return this.templates.find((item) => item.id === this.selectedTemplateId) || null;
    },
    requiredNodes() {
      return Math.max(1, Number(this.selectedTemplate?.min_nodes || 1));
    },
    recommendedSpec() {
      const requested = String(this.selectedTemplate?.minimum_spec || "1C2G").toUpperCase();
      const supported = ["1C2G", "2C2G", "2C4G", "2C8G", "4C8G", "4C16G", "4C32G"];
      const match = requested.match(/(\d+)C(\d+)G/);
      if (!match) return "2C4G";
      const cpu = Number(match[1]);
      const memory = Number(match[2]);
      return supported.find((spec) => {
        const parts = spec.match(/(\d+)C(\d+)G/);
        return Number(parts[1]) >= cpu && Number(parts[2]) >= memory;
      }) || "4C32G";
    },
    primaryActionText() {
      if (this.isDeploymentActive) return "已有任务执行中";
      if (this.provisioning) return this.provisionStatusText;
      return this.resources.instances.length ? "检查并部署" : "创建实例并部署";
    },
    selectedInstanceLabel() {
      const instance = this.resources.instances.find((item) => item.instance_id === this.selectedInstanceId);
      return instance ? `${instance.label}（${instance.public_ipv4 || instance.private_ipv4}）` : "尚未选择";
    },
    selectedTargetIds() {
      return this.requiredNodes === 1
        ? (this.selectedInstanceId ? [this.selectedInstanceId] : [])
        : this.selectedInstanceIds.slice(0, this.requiredNodes);
    },
    selectedTargetLabel() {
      const labels = this.selectedTargetIds.map((id) => this.resources.instances.find((item) => item.instance_id === id)?.label).filter(Boolean);
      return labels.length ? labels.join("、") : `请选择 ${this.requiredNodes} 台实例`;
    },
    hasEnoughTargets() {
      return this.selectedTargetIds.length === this.requiredNodes;
    },
    displayDistribution() {
      const profile = this.resources.profile || {};
      return [profile.distribution, profile.version].filter(Boolean).join(" ") || "系统未知";
    },
    isDeploymentActive() {
      return this.currentDeployment && ["QUEUED", "PREFLIGHT", "RUNNING"].includes(this.currentDeployment.status);
    },
    deploymentStatusText() {
      const names = { QUEUED: "等待执行", PREFLIGHT: "检查部署条件", RUNNING: "正在配置环境", SUCCEEDED: "部署成功", FAILED: "部署失败" };
      return names[this.currentDeployment?.status] || "任务状态";
    },
    deploymentTagType() {
      return this.statusTag(this.currentDeployment?.status);
    },
    infoCard() {
      return this.currentDeployment?.result?.info_card || null;
    },
    infoCardEntries() {
      if (!this.infoCard) return [];
      const labels = {
        access_url: "访问地址", config_path: "配置文件", log_path: "日志目录",
        check_command: "检查命令", socket: "Docker Socket", instance: "部署实例",
        deployed_at: "部署时间", note: "使用提示",
      };
      return Object.entries(this.infoCard)
        .filter(([key]) => labels[key])
        .map(([key, value]) => ({ key, label: labels[key], value, copyable: ["access_url", "config_path", "log_path", "check_command"].includes(key) }));
    },
  },
  mounted() {
    this.refreshAll();
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopProvisionPolling();
    this.stopDataPolling();
  },
  methods: {
    async refreshAll() {
      this.loading = true;
      try {
        const [templates, resources, history, activeScenarios] = await Promise.all([
          axios.get("/api/scenarios/templates"),
          axios.get("/api/scenarios/resources"),
          axios.get("/api/scenarios/deployments"),
          axios.get("/api/scenarios/active"),
        ]);
        this.templates = templates.data || [];
        this.resources = resources.data || emptyResources();
        this.history = history.data || [];
        this.activeScenarios = activeScenarios.data || [];
        if (!this.selectedTemplateId && this.templates.length) this.selectTemplate(this.templates[0].id);
        if (!this.selectedInstanceId && this.resources.instances.length) this.selectedInstanceId = this.resources.instances[0].instance_id;
        this.syncSelectedTargets();
        const active = this.history.find((item) => ["QUEUED", "PREFLIGHT", "RUNNING"].includes(item.status));
        const latestSuccess = this.history.find((item) => item.status === "SUCCEEDED");
        if (active) this.startPolling(active.deployment_id, false);
        else if (latestSuccess && !this.currentDeployment) await this.loadDeployment(latestSuccess.deployment_id);
      } catch (error) {
        ElMessage.error(error.response?.data?.message || "无法加载场景部署信息");
      } finally {
        this.loading = false;
      }
    },
    selectTemplate(templateId) {
      this.selectedTemplateId = templateId;
      const template = this.templates.find((item) => item.id === templateId);
      this.selectedSoftwareVersion = template?.default_software_version || template?.software_versions?.[0]?.value || "";
      this.preflightResult = null;
      this.syncSelectedTargets();
    },
    templateInitial(template) {
      const initials = { docker: "D", nginx: "N", rabbitmq: "R", podman: "P", redis: "R", mysql: "M", ansible: "A", prometheus: "P", postgresql: "PG", java: "J", elasticsearch: "ES", kubernetes: "K8s", k3s: "K3s", minio: "MI", grafana: "G", jenkins: "J", haproxy: "HA", tomcat: "T", mongodb: "M", zookeeper: "ZK", neo4j: "N4J", clickhouse: "CH", zabbix: "Z", lvs: "LVS" };
      return initials[template.icon] || String(template.name || "S").slice(0, 1).toUpperCase();
    },
    async interpretPrompt() {
      if (!this.prompt.trim()) return ElMessage.warning("请先描述需要的环境");
      this.interpreting = true;
      try {
        const response = await axios.post("/api/scenarios/interpret", { prompt: this.prompt });
        if (!response.data.matched) return ElMessage.warning(response.data.message);
        this.selectTemplate(response.data.template_id);
        ElMessage.success(`已识别：${this.selectedTemplate?.name || response.data.template_id}`);
      } catch (error) {
        ElMessage.error(error.response?.data?.message || "场景识别失败");
      } finally {
        this.interpreting = false;
      }
    },
    async prepareDeployment(autoConfirmed = false) {
      this.checking = true;
      try {
        const response = await axios.post("/api/scenarios/preflight", {
          template_id: this.selectedTemplateId,
          instance_ids: this.selectedTargetIds,
          software_version: this.selectedSoftwareVersion || undefined,
        });
        this.preflightResult = response.data;
        if (!response.data.passed) return ElMessage.warning("当前环境不满足部署条件");
        if (!autoConfirmed) {
          const feeText = response.data.vip_free ? "VIP 用户本次免基础场景服务费。" : `部署成功后扣除 ${response.data.service_fee_points} 积分。`;
          await ElMessageBox.confirm(`${feeText} 平台部署失败将自动解冻积分，是否继续？`, "确认场景部署", {
            confirmButtonText: "开始部署", cancelButtonText: "取消", type: "warning",
          });
        }
        await this.submitDeployment();
      } catch (error) {
        if (error === "cancel" || error === "close") return;
        const data = error.response?.data;
        if (data?.checks) this.preflightResult = data;
        ElMessage.error(data?.message || "部署检查失败");
      } finally {
        this.checking = false;
      }
    },
    syncSelectedTargets() {
      const availableIds = this.resources.instances.map((item) => item.instance_id);
      if (this.requiredNodes === 1) {
        if (!availableIds.includes(this.selectedInstanceId)) this.selectedInstanceId = availableIds[0] || "";
        this.selectedInstanceIds = [];
        return;
      }
      this.selectedInstanceIds = this.selectedInstanceIds.filter((id) => availableIds.includes(id)).slice(0, this.requiredNodes);
      for (const id of availableIds) {
        if (this.selectedInstanceIds.length >= this.requiredNodes) break;
        if (!this.selectedInstanceIds.includes(id)) this.selectedInstanceIds.push(id);
      }
    },
    handlePrimaryAction() {
      if (this.resources.instances.length) return this.prepareDeployment();
      return this.createInstanceAndDeploy();
    },
    scrollToConfiguration() {
      document.querySelector(".console-shell")?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async createInstanceAndDeploy() {
      if (!this.selectedTemplate) return ElMessage.warning("请先选择部署场景");
      if (this.requiredNodes > 5) return ElMessage.warning("该场景节点数超过当前自动创建上限");
      this.checking = true;
      const payload = {
        id: 30066,
        instanceCount: this.requiredNodes,
        systemDiskSize: 40,
        selectedDist: "Ubuntu",
        selectedVersion: "24.04",
        selectedSpec: this.recommendedSpec,
      };
      try {
        const quoteResponse = await axios.post("/api/multi_instances_quote", payload);
        const quote = quoteResponse.data;
        if (!quote.can_create) {
          return ElMessageBox.alert(
            `创建需要预冻结 ${quote.reserve_points} 积分，当前可用 ${quote.available_points} 积分，请先充值。`,
            "积分不足",
            { confirmButtonText: "知道了", type: "warning" }
          );
        }
        await ElMessageBox.confirm(
          `将创建 ${payload.instanceCount} 台 Ubuntu 24.04 LTS（${payload.selectedSpec}、40GB 系统盘），云资源按 ${quote.points_per_hour} 积分/小时计费并预冻结 ${quote.reserve_points} 积分。创建成功后将自动部署「${this.selectedTemplate.name}」。所有用户均享受 VIP 启动费和基础场景费减免。是否继续？`,
          "确认创建并部署",
          { confirmButtonText: "创建并自动部署", cancelButtonText: "取消", type: "warning" }
        );
        this.provisioning = true;
        this.provisionStatusText = "正在提交创建任务";
        try {
          const response = await axios.post("/api/create_multi_instances", payload);
          this.provisionJobId = response.data.job_id;
        } catch (error) {
          if (error.response?.status === 409 && error.response?.data?.job_id) {
            this.provisionJobId = error.response.data.job_id;
          } else {
            throw error;
          }
        }
        if (!this.provisionJobId) throw new Error("创建任务编号缺失");
        this.provisionStatusText = "实例正在创建";
        this.pollProvisionForDeployment();
      } catch (error) {
        if (error === "cancel" || error === "close") return;
        this.provisioning = false;
        ElMessage.error(error.response?.data?.message || error.response?.data?.error || error.message || "虚拟机创建失败");
      } finally {
        this.checking = false;
      }
    },
    async pollProvisionForDeployment() {
      this.stopProvisionPolling();
      const poll = async () => {
        try {
          const response = await axios.get(`/api/multi_instance_jobs/${this.provisionJobId}`);
          const job = response.data;
          if (job.status === "FAILED") {
            this.stopProvisionPolling();
            this.provisioning = false;
            this.provisionStatusText = "创建失败";
            return ElMessage.error(job.message || "虚拟机创建失败，预冻结积分已退回");
          }
          if (job.status !== "READY") {
            this.provisionStatusText = job.status === "RUNNING" ? "云资源初始化中" : "等待云资源创建";
            return;
          }
          this.stopProvisionPolling();
          this.provisionStatusText = "实例就绪，准备部署";
          await this.waitForScenarioResources();
          this.provisioning = false;
          ElMessage.success("Ubuntu 24.04 实例已就绪，开始场景部署");
          await this.prepareDeployment(true);
        } catch (error) {
          this.stopProvisionPolling();
          this.provisioning = false;
          ElMessage.error(error.response?.data?.message || "查询虚拟机创建进度失败");
        }
      };
      await poll();
      if (this.provisioning) this.provisionPollTimer = window.setInterval(poll, 1800);
    },
    stopProvisionPolling() {
      if (this.provisionPollTimer) window.clearInterval(this.provisionPollTimer);
      this.provisionPollTimer = null;
    },
    async waitForScenarioResources() {
      for (let attempt = 0; attempt < 10; attempt += 1) {
        const response = await axios.get("/api/scenarios/resources");
        this.resources = response.data || emptyResources();
        if (this.resources.instances.length) {
          this.selectedInstanceId = this.resources.instances[0].instance_id;
          this.syncSelectedTargets();
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
      }
      throw new Error("实例已创建，但场景服务尚未读取到资源，请点击刷新后重试");
    },
    async submitDeployment() {
      this.submitting = true;
      try {
        const response = await axios.post("/api/scenarios/deployments", {
          template_id: this.selectedTemplateId,
          instance_ids: this.selectedTargetIds,
          software_version: this.selectedSoftwareVersion || undefined,
        });
        ElMessage.success("部署任务已进入队列");
        this.activeTab = "deployments";
        this.startPolling(response.data.deployment_id, true);
      } catch (error) {
        ElMessage.error(error.response?.data?.message || "部署任务创建失败");
      } finally {
        this.submitting = false;
      }
    },
    async loadDeployment(deploymentId) {
      const response = await axios.get(`/api/scenarios/deployments/${deploymentId}`);
      this.currentDeployment = response.data;
      return response.data;
    },
    async openHistoryDeployment(row) {
      await this.loadDeployment(row.deployment_id);
      this.expandedDeploymentSections = [];
      this.deploymentDrawer = true;
    },
    startPolling(deploymentId, openDrawer = true) {
      this.stopPolling();
      if (openDrawer) this.deploymentDrawer = true;
      const poll = async () => {
        try {
          const deployment = await this.loadDeployment(deploymentId);
          if (["SUCCEEDED", "FAILED", "CANCELLED"].includes(deployment.status)) {
            this.stopPolling();
            await this.refreshHistory();
            ElMessage({
              message: deployment.status === "SUCCEEDED" ? "场景部署成功" : (deployment.error_message || "场景部署失败，积分已解冻"),
              type: deployment.status === "SUCCEEDED" ? "success" : "error",
            });
          }
        } catch (error) {
          this.stopPolling();
          ElMessage.error(error.response?.data?.message || "部署状态查询失败");
        }
      };
      poll();
      this.pollTimer = window.setInterval(poll, 2000);
    },
    stopPolling() {
      if (this.pollTimer) window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    async refreshHistory() {
      const response = await axios.get("/api/scenarios/deployments");
      this.history = response.data || [];
      await this.loadActiveScenarios();
    },
    async loadActiveScenarios() {
      this.loadingActiveScenarios = true;
      try {
        const response = await axios.get("/api/scenarios/active");
        this.activeScenarios = response.data || [];
      } catch (error) {
        ElMessage.error(error.response?.data?.message || "无法检测活跃场景");
      } finally {
        this.loadingActiveScenarios = false;
      }
    },
    toggleSecret(key) {
      this.revealedSecrets = { ...this.revealedSecrets, [key]: !this.revealedSecrets[key] };
    },
    openDataInjection(scene) {
      this.dataTargetScene = scene;
      this.dataForm = { dataset_key: "ecommerce", dataset_size: "small" };
      this.dataDialogVisible = true;
    },
    async submitDataInjection() {
      if (!this.dataTargetScene) return;
      this.injectingData = true;
      try {
        await ElMessageBox.confirm("将重建该场景的 sre_shop 演示数据，是否继续？", "确认数据注入", { confirmButtonText: "继续", cancelButtonText: "取消", type: "warning" });
        await axios.post(`/api/scenarios/active/${this.dataTargetScene.deployment_id}/data`, this.dataForm);
        this.dataDialogVisible = false;
        ElMessage.success("数据注入任务已进入队列");
        this.startDataPolling();
      } catch (error) {
        if (error === "cancel" || error === "close") return;
        ElMessage.error(error.response?.data?.message || "数据注入任务创建失败");
      } finally {
        this.injectingData = false;
      }
    },
    startDataPolling() {
      this.stopDataPolling();
      const poll = async () => {
        await this.loadActiveScenarios();
        const targetScenes = this.activeScenarios.filter((scene) => scene.deployment_id === this.dataTargetScene?.deployment_id);
        const running = targetScenes.some((scene) => ["QUEUED", "RUNNING"].includes(scene.data_job?.status));
        if (!running) {
          this.stopDataPolling();
          const failed = targetScenes.find((scene) => scene.data_job?.status === "FAILED");
          ElMessage({ type: failed ? "error" : "success", message: failed?.data_job?.error_message || "演示数据注入完成" });
        }
      };
      this.dataPollTimer = window.setInterval(poll, 2000);
      poll();
    },
    stopDataPolling() {
      if (this.dataPollTimer) window.clearInterval(this.dataPollTimer);
      this.dataPollTimer = null;
    },
    dataJobTag(status) {
      return status === "SUCCEEDED" ? "success" : status === "FAILED" ? "danger" : "warning";
    },
    dataJobText(job) {
      const names = { QUEUED: "等待执行", RUNNING: "正在写入", SUCCEEDED: "注入完成", FAILED: "注入失败" };
      return `${names[job.status] || job.status} · ${job.requested_rows} 笔`;
    },
    statusTag(status) {
      if (status === "SUCCEEDED") return "success";
      if (status === "FAILED") return "danger";
      if (["RUNNING", "PREFLIGHT"].includes(status)) return "warning";
      return "info";
    },
    async copyText(text) {
      await navigator.clipboard.writeText(String(text));
      ElMessage.success("已复制");
    },
  },
};
</script>

<style scoped>
.scenario-page { min-height: calc(100vh - 70px); padding: 20px; background: #f4f7fb; color: #172235; text-align: left; }
.page-header, .resource-summary, .resource-details, .console-shell { max-width: 1180px; margin: 0 auto; }
.page-header { min-height: 66px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.page-header h1 { margin: 3px 0 0; color: #173447; font-size: 25px; }.header-actions { display: flex; gap: 9px; }
.eyebrow { color: #2b857e; font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.resource-summary { min-height: 52px; padding: 0 18px; display: flex; align-items: center; justify-content: space-between; gap: 14px; background: #fff; border: 1px solid #e0e8ef; border-radius: 11px; box-shadow: 0 5px 16px rgba(37, 55, 86, .04); }
.resource-main { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; color: #667587; font-size: 13px; }.resource-main strong { color: #26384a; }.resource-main span + span { padding-left: 10px; border-left: 1px solid #dfe6ed; }
.status-dot { width: 8px; height: 8px; border-radius: 50%; background: #aab4bf; }.status-dot.online { background: #2ca678; box-shadow: 0 0 0 4px rgba(44, 166, 120, .12); }
.resource-details { margin-top: 8px; padding: 14px 18px; background: #fff; border: 1px solid #e1e8ef; border-radius: 11px; }.release-time { margin-bottom: 10px; color: #748194; font-size: 12px; }
.instance-chips { display: flex; flex-wrap: wrap; gap: 9px; }.instance-chip { padding: 9px 13px; display: flex; flex-direction: column; gap: 3px; border: 1px solid #dfe6ed; border-radius: 8px; background: #fafbfd; color: #617082; text-align: left; cursor: pointer; }.instance-chip strong { color: #26384a; }.instance-chip.selected { border-color: #39958c; background: #f0faf8; }
.console-shell { margin-top: 14px; background: #fff; border: 1px solid #e0e8ef; border-radius: 13px; box-shadow: 0 8px 24px rgba(37, 55, 86, .05); overflow: hidden; }.console-tabs :deep(.el-tabs__header) { margin: 0; padding: 0 22px; }.console-tabs :deep(.el-tabs__content) { padding: 0; }.console-tabs :deep(.el-tabs__item) { height: 52px; }
.deploy-workspace { min-height: 570px; display: grid; grid-template-columns: 330px minmax(0, 1fr); border-top: 1px solid #e7edf2; }
.catalog-panel { border-right: 1px solid #e5ebf0; background: #fafbfd; }.catalog-tools { padding: 15px; display: grid; grid-template-columns: minmax(0, 1fr) 112px; gap: 8px; border-bottom: 1px solid #e7edf2; }.category-select { width: 112px; }.catalog-count { padding: 11px 16px 6px; color: #8995a5; font-size: 12px; }
.template-list { height: 472px; padding: 5px 9px 12px; overflow-y: auto; }.template-row { width: 100%; padding: 12px 10px; display: flex; align-items: center; gap: 11px; border: 1px solid transparent; border-radius: 9px; background: transparent; text-align: left; cursor: pointer; }.template-row:hover { background: #fff; border-color: #e3e9ef; }.template-row.selected { background: #eef8f6; border-color: #c9e2de; }
.template-icon { width: 36px; height: 36px; flex: 0 0 36px; display: grid; place-items: center; border-radius: 9px; background: #607d9a; color: #fff; font-size: 12px; font-weight: 900; }.template-icon.docker { background: #247bc1; }.template-icon.nginx { background: #249263; }.template-icon.redis { background: #c64a43; }.template-icon.mysql { background: #397aa4; }.template-icon.rabbitmq { background: #e7832e; }.template-icon.podman { background: #7254a3; }.template-icon.ansible { background: #303a45; }.template-icon.prometheus { background: #d55b3d; }.template-icon.postgresql { background: #416d99; }.template-row-copy { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 4px; }.template-row-copy strong { color: #23364a; }.template-row-copy small { color: #8390a0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }.row-arrow { color: #91a0ae; font-size: 21px; }
.config-panel { min-width: 0; padding: 24px 28px; }.config-head { display: flex; justify-content: space-between; gap: 20px; }.config-head h2 { margin: 5px 0 6px; font-size: 24px; }.config-head p { margin: 0; color: #6d7c8d; }.feature-list { min-height: 40px; margin: 17px 0; display: flex; flex-wrap: wrap; gap: 7px; }
.config-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 9px; }.config-item { padding: 13px; display: flex; flex-direction: column; gap: 4px; background: #f7f9fb; border: 1px solid #e8edf2; border-radius: 9px; }.config-item span, .config-item small { color: #8995a5; font-size: 11px; }.config-item strong { color: #293c4f; }
.target-section { margin-top: 20px; padding-top: 18px; border-top: 1px solid #e7edf2; }.field-label { margin-bottom: 11px; color: #3b4c5f; font-weight: 700; }.target-section .el-radio-group, .target-section .el-checkbox-group { display: flex; flex-wrap: wrap; gap: 9px; }.target-section :deep(.el-radio), .target-section :deep(.el-checkbox) { margin-right: 0; }
.auto-provision-card { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border: 1px solid #cfe2df; border-radius: 9px; background: #f2faf8; }.auto-provision-card strong { color: #24544f; }.auto-provision-card p { margin: 5px 0 0; color: #687b7a; font-size: 12px; line-height: 1.55; }
.check-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-top: 18px; }.check-item { min-height: 40px; padding: 9px 11px; display: flex; align-items: center; gap: 8px; border-radius: 8px; background: #edf9f5; color: #17654e; }.check-item.failed { background: #fff2f0; color: #a53b32; }.check-item p { margin: 0; color: inherit; font-size: 12px; }
.deploy-footer { margin-top: 22px; padding-top: 17px; display: flex; align-items: center; justify-content: space-between; gap: 15px; border-top: 1px solid #e7edf2; }.deploy-footer span { color: #728092; font-size: 13px; }
.ai-workspace { min-height: 570px; padding: 54px 70px; display: grid; grid-template-columns: .9fr 1.1fr; gap: 65px; align-items: center; border-top: 1px solid #e7edf2; background: linear-gradient(135deg, #fff, #f4faf9); }.ai-copy h2 { margin: 8px 0; font-size: 28px; }.ai-copy p { color: #6d7c8d; line-height: 1.7; }.prompt-examples { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 20px; }.prompt-examples button { padding: 7px 12px; border: 1px solid #d7e5e2; border-radius: 16px; background: #fff; color: #397b75; cursor: pointer; }.intent-box { padding: 22px; background: #fff; border: 1px solid #dfe9e7; border-radius: 13px; box-shadow: 0 14px 34px rgba(48, 86, 82, .08); }.intent-box > .el-button { width: 100%; margin-top: 12px; }.recognized-plan { margin-top: 14px; padding: 12px; display: grid; grid-template-columns: 1fr auto; gap: 4px 12px; background: #f0f8f6; border-radius: 8px; }.recognized-plan span { grid-column: 1 / -1; color: #7a8a91; font-size: 11px; }
.active-scenes-view { min-height: 570px; padding: 22px; border-top: 1px solid #e7edf2; background: #f8fafc; }.active-scenes-head { margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; gap: 18px; }.active-scenes-head h2 { margin: 0 0 4px; font-size: 21px; }.active-scenes-head p { margin: 0; color: #7c8998; font-size: 13px; }.active-scene-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }.active-scene-card { min-width: 0; padding: 18px; border: 1px solid #dce6ea; border-radius: 12px; background: #fff; box-shadow: 0 6px 18px rgba(37,55,86,.04); }.scene-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }.scene-card-head h3 { margin: 5px 0 0; }.scene-meta { margin: 10px 0 14px; display: flex; flex-wrap: wrap; gap: 6px 14px; color: #8994a2; font-size: 11px; }.scene-address, .credential-box>div, .connection-list>div { min-width: 0; padding: 8px 0; display: grid; grid-template-columns: 72px minmax(0,1fr) auto; align-items: center; gap: 8px; border-top: 1px solid #edf1f4; }.scene-address span, .credential-box span, .connection-list span { color: #7b8794; font-size: 12px; }.scene-address code, .credential-box code, .connection-list code { min-width: 0; overflow-wrap: anywhere; color: #26384a; font-size: 12px; }.credential-box { margin: 8px 0; padding: 0 10px; border: 1px solid #dbe9e6; border-radius: 8px; background: #f4faf8; }.connection-list { margin-bottom: 12px; }
.data-job-status { margin: 10px 0; padding: 10px; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; border-radius: 8px; background: #f6f8fb; }.data-job-status>span { color: #677587; font-size: 12px; font-weight: 700; }.data-job-status small { width: 100%; color: #718092; }.data-job-status .data-error { color: #b33d34; }.inject-data-button { width: 100%; margin-top: 4px; }
.deployments-view { min-height: 570px; padding: 22px; border-top: 1px solid #e7edf2; }.active-task-card { margin-bottom: 18px; padding: 17px 20px; display: flex; align-items: center; justify-content: space-between; border: 1px solid #cfe2df; border-radius: 11px; background: #f4faf9; cursor: pointer; }.active-task-card h3 { margin: 4px 0; }.active-task-card p { margin: 0; color: #728092; }.task-progress { display: flex; align-items: center; gap: 12px; }
.history-hint { margin-bottom: 10px; padding: 9px 12px; border-radius: 8px; background: #f0f7ff; color: #53708d; font-size: 12px; }.clickable-deployment-table :deep(.el-table__row) { cursor: pointer; transition: background-color .16s ease, transform .16s ease; }.clickable-deployment-table :deep(.el-table__row:hover td) { background: #eef7f6 !important; }.clickable-deployment-table :deep(.el-table__row:hover .el-button) { text-decoration: underline; }
.floating-task { position: fixed; right: 24px; bottom: 24px; z-index: 20; padding: 11px 16px; display: flex; align-items: center; gap: 9px; border: 0; border-radius: 22px; background: #236f69; color: #fff; box-shadow: 0 10px 28px rgba(25, 81, 76, .25); cursor: pointer; }.pulse { width: 8px; height: 8px; border-radius: 50%; background: #8ff1c8; box-shadow: 0 0 0 5px rgba(143, 241, 200, .18); }
.drawer-status { margin-bottom: 20px; display: flex; justify-content: space-between; gap: 15px; }.drawer-status h2 { margin: 5px 0; }.drawer-status p { margin: 0; color: #718092; }.deployment-details-collapse { margin-top: 18px; }.timeline { max-height: 330px; padding-left: 14px; overflow-y: auto; border-left: 2px solid #dbe4ec; }.event { padding: 9px 5px; display: grid; grid-template-columns: 68px 1fr; gap: 10px; font-size: 12px; }.event time { color: #8a96a5; }.event div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }.event div span { color: #6c7988; word-break: break-word; }.event code { padding: 6px 8px; overflow-wrap: anywhere; border-radius: 5px; background: #1f2937; color: #d7f7e8; }.event.error, .event.error span { color: #a53b32; }
.result-card { margin-top: 20px; padding: 16px; border: 1px solid #cbe2dc; border-radius: 10px; background: #f6fbfa; }.result-head { display: flex; align-items: center; justify-content: space-between; }.result-head h3 { margin: 0; }.result-row { padding: 10px 0; display: grid; grid-template-columns: 82px 1fr auto; gap: 8px; border-bottom: 1px solid #e1ebe8; }.result-row:last-child { border: 0; }.result-row span { color: #7b8794; font-size: 12px; }.result-row strong { word-break: break-all; font-size: 12px; }
@media (max-width: 860px) { .scenario-page { padding: 12px; }.page-header { min-height: 58px; }.deploy-workspace { display: block; }.catalog-panel { border-right: 0; border-bottom: 1px solid #e5ebf0; }.template-list { height: 250px; }.config-panel { padding: 20px 16px; }.config-grid, .active-scene-grid { grid-template-columns: repeat(2, 1fr); }.ai-workspace { padding: 30px 20px; display: block; }.intent-box { margin-top: 24px; }.resource-main span:nth-of-type(n+4) { display: none; } }
@media (max-width: 560px) { .page-header h1 { font-size: 21px; }.header-actions .el-button:first-child { display: none; }.catalog-tools { grid-template-columns: 1fr; }.category-select { width: 100%; }.config-grid, .check-list, .active-scene-grid { grid-template-columns: 1fr; }.active-scenes-head { align-items: flex-start; flex-direction: column; }.auto-provision-card { align-items: flex-start; flex-direction: column; }.deploy-footer { align-items: stretch; flex-direction: column; }.deploy-footer .el-button { width: 100%; }.scenario-page :deep(.el-drawer) { width: 94% !important; } }
</style>
